import React, { useState, useEffect } from "react";
import { fromJS } from "immutable";
import { PropertyPanelSection } from "@showitinc/showit-wc";
import { merge, cloneDeep } from "lodash";

import UIActions from "../../actions/UIActions";
import styles from "./DesignSettingsButtons.module.css";
import DesignSettingsCustomizeMenu from "./DesignSettingsCustomizeMenu";
import ButtonStateTabset from "@/app2components/ButtonStateTabset/ButtonStateTabset";
import { MapElementProperties } from "@/utils/Element/ElementPropertyMapper";
import DynamicInput from "../../app2components/base/DynamicInput";
import useStyleSync from "../../hooks/useStyleSync";

import AppConstants from "../../constants/AppConstants";
import PropertyMaps from "../../constants/PropertyMaps";
import {
  BUTTON_PROPERTY_META_MAP,
  DEFAULT_BUTTON_VALUES,
} from "@/utils/Element/ButtonPropertyMaps";
import { DEFAULT_EFFECTS_VALUES } from "@/utils/Element/EffectsPropertyMaps";
import {
  HOVER,
  BLUR,
  HOVER_BLUR,
  ELEMENT_BACKGROUND_FILL_TYPE,
  PADDING,
  BUTTON_TEXT_COLOR,
  BUTTON_TEXT_FONT,
  BUTTON_TEXT_SIZE,
  BUTTON_HOVER_DURATION,
  BUTTON_HOVER_FILL_TYPE,
  BUTTON_BORDER_RADIUS,
} from "@/constants/MergeConstants";
import { LAYOUT_MOBILE, LAYOUT_DESKTOP } from "../../constants/DesignConstants";
import { useAccountState } from "@/hooks/query/useAccount";

const {
  BUTTON_PANEL_STATE_PRIMARY,
  BUTTON_PANEL_TAB_STATE_HOVER,
  BUTTON_UI_STATE_PRIMARY_TAB,
  BUTTON_UI_STATE_SECONDARY_TAB,
  BUTTON_UI_STATE_SELECTED_PANEL,
  BUTTON_STATE_HOVER,
  THEME_STYLES,
} = AppConstants;

const { STYLE_PROPS_THEME_BUTTONS, STYLE_PROPS_BUTTON_TEXT } = PropertyMaps;

/**
 * Configuration for compound theme properties.
 */
const COMPOUND_THEME_PROPS = {
  shadow: {
    sentinelProp: "style",
  },
};

/**
 * Handles the specific case of updating a compound property on a hover state
 * that has not yet been customized.
 * @returns A specific update object if the condition is met, otherwise null.
 */
const handleHoverCompoundPropertyUpdate = ({
  propName,
  userValue,
  compoundProps,
  buttonStyles,
  selectedLayout,
}) => {
  if (!propName.includes(".hover.")) {
    return null;
  }

  const buttonType = propName.includes(".primary.") ? "primary" : "secondary";

  const relevantCompoundPropKey = Object.keys(compoundProps).find((propKey) =>
    propName.includes(`.hover.${propKey}.`),
  );

  if (relevantCompoundPropKey) {
    const propConfig = compoundProps[relevantCompoundPropKey];
    const hoverObjPath = [buttonType, selectedLayout, "hover", relevantCompoundPropKey];
    const hoverSentinelPath = [...hoverObjPath, propConfig.sentinelProp];
    const hoverSentinelValue = buttonStyles.getIn(hoverSentinelPath);

    if (hoverSentinelValue === undefined) {
      const desktopPropPath = [buttonType, selectedLayout, relevantCompoundPropKey];
      const desktopPropValue = buttonStyles.getIn(desktopPropPath);

      if (desktopPropValue) {
        const subPropPath = propName.split(`.hover.${relevantCompoundPropKey}.`)[1];
        const newHoverProp = desktopPropValue.setIn(subPropPath.split("."), userValue);
        return {
          name: `${THEME_STYLES}.${buttonType}.${selectedLayout}.hover.${relevantCompoundPropKey}`,
          value: newHoverProp,
          source: "propertyInput",
        };
      }
    }
  }

  return null;
};

/**
 * Creates a prop change handler that can intelligently handle compound theme properties.
 */
const createThemePropChangeHandler =
  ({ onChange, buttonStyles, selectedLayout, compoundProps }) =>
  (propInfo) => {
    const updates = !Array.isArray(propInfo) ? [propInfo] : propInfo;
    const userChange = updates[0];
    if (!userChange || !userChange.name) {
      onChange(updates);
      return;
    }

    const { name: propName, value: userValue } = userChange;

    const hoverUpdate = handleHoverCompoundPropertyUpdate({
      propName,
      userValue,
      compoundProps,
      buttonStyles,
      selectedLayout,
    });

    if (hoverUpdate) {
      onChange([hoverUpdate]);
      return;
    }

    onChange(updates);
  };

/**
 * Calculates the effective property name for removal, handling compound properties.
 */
const getEffectivePropNameForRemoval = (propName, compoundProps) => {
  const matchingPropKey = Object.keys(compoundProps).find((propKey) => {
    const propConfig = compoundProps[propKey];
    const sentinelPropPath = `.${propKey}.${propConfig.sentinelProp}`;
    return propName === sentinelPropPath;
  });

  return matchingPropKey ? `.${matchingPropKey}` : propName;
};

const DEFAULT_NORMAL_TAB_PROPS = [
  ELEMENT_BACKGROUND_FILL_TYPE,
  PADDING,
  BUTTON_TEXT_COLOR,
  BUTTON_TEXT_FONT,
  BUTTON_TEXT_SIZE,
];
const DEFAULT_HOVER_TAB_PROPS = [BUTTON_HOVER_DURATION, BUTTON_HOVER_FILL_TYPE];

const buttonPropMetaMapOverride = merge(cloneDeep(BUTTON_PROPERTY_META_MAP), {
  [BUTTON_BORDER_RADIUS]: {
    shift: "right",
    usePresets: false,
  },
  [BUTTON_HOVER_DURATION]: {
    context: false,
  },
  [BUTTON_HOVER_FILL_TYPE]: {
    disabled: true,
  },
});

/**
 *
 * @param {string} propName
 * @returns
 */
const buttonStateFilter = (propName, buttonState) =>
  buttonState === BUTTON_STATE_HOVER ? propName.startsWith(HOVER) : !propName.startsWith(HOVER);

/**
 * Manages button styles in design settings
 * @param {Function} onChange - prop update handler
 * @param {Object} themeStyles - theme styles map
 * @param {Object} theme - full theme object
 * @param {string} selectedLayout - current layout (mobile or desktop)
 * @param {Object} uiState - current UI state
 * @param {Function} setUiState - UI state setter
 */
const DesignSettingsButtonsStyles = ({
  onChange,
  themeStyles,
  selectedLayout,
  uiState,
  setUiState,
  palette,
}) => {
  const user = useAccountState();
  const isAdmin = user.get("isAdmin", false);
  const [pendingCustomizations, setPendingCustomizations] = useState({
    primary: [],
    secondary: [],
  });

  // Reset pending customizations when theme is reset
  useEffect(() => {
    const resetHandler = () => {
      setPendingCustomizations({
        primary: [],
        secondary: [],
      });
    };
    document.addEventListener("resetThemePanel", resetHandler);
    return () => document.removeEventListener("resetThemePanel", resetHandler);
  }, []);

  const buttonStyles = fromJS({
    primary: themeStyles.get("primary"),
    secondary: themeStyles.get("secondary"),
  });
  const buttonsTypeMap = ["primary", "secondary"];

  const onPropChange = createThemePropChangeHandler({
    onChange,
    buttonStyles,
    selectedLayout,
    compoundProps: COMPOUND_THEME_PROPS,
  });

  const primaryStyleSync = useStyleSync({
    parentType: THEME_STYLES,
    childType: "primary",
    styles: buttonStyles.get("primary"),
    selectedLayout,
    onChange: onPropChange,
    propKeys: [...STYLE_PROPS_THEME_BUTTONS, ...STYLE_PROPS_BUTTON_TEXT],
  });

  const secondaryStyleSync = useStyleSync({
    parentType: THEME_STYLES,
    childType: "secondary",
    styles: buttonStyles.get("secondary"),
    selectedLayout,
    onChange: onPropChange,
    propKeys: [...STYLE_PROPS_THEME_BUTTONS, ...STYLE_PROPS_BUTTON_TEXT],
  });

  const getStyleSync = (buttonType) =>
    buttonType === "primary" ? primaryStyleSync : secondaryStyleSync;

  const createThemeChangeHandler = (buttonType) => {
    const styleSync = getStyleSync(buttonType);
    const { onFormatChange, prepChange } = styleSync;

    return (propName) => (eventValue) => {
      setPendingCustomizations((prev) => ({
        ...prev,
        [buttonType]: prev[buttonType].filter((prop) => prop !== propName),
      }));

      const handler = onFormatChange(...prepChange(propName), false);
      handler(eventValue);
    };
  };

  const handleAddCustomization = (buttonType, propName) => {
    setPendingCustomizations((prev) => ({
      ...prev,
      [buttonType]: [...prev[buttonType], propName],
    }));

    // Ensure property is synced (merged) when added from dropdown
    // This handles themes that don't have new properties in their sync array
    const styleSync = getStyleSync(buttonType);
    if (!styleSync.isSynced(propName)) {
      const baseProp = propName.startsWith(HOVER) ? propName.slice(HOVER.length) : propName;
      const defaultValue = DEFAULT_EFFECTS_VALUES[baseProp];
      const pathPrefix = `${THEME_STYLES}.${buttonType}.`;
      styleSync.toggleSyncedProp(propName, pathPrefix, defaultValue);
    }
  };

  const handleRemoveCustomization = (buttonType, propName) => {
    const isPending = pendingCustomizations[buttonType]?.includes(propName);

    if (isPending) {
      setPendingCustomizations((prev) => ({
        ...prev,
        [buttonType]: prev[buttonType].filter((prop) => prop !== propName),
      }));
      return;
    }

    const isHover = propName.startsWith(HOVER);
    let prop = isHover ? propName.substring(HOVER.length) : propName;

    prop = getEffectivePropNameForRemoval(prop, COMPOUND_THEME_PROPS);

    const desktopPath = `${THEME_STYLES}.${buttonType}.${LAYOUT_DESKTOP}.${
      isHover ? `${HOVER}${prop}` : prop
    }`;
    const updates = [{ name: desktopPath, value: null }];

    if (!isHover) {
      updates.push({
        name: `${THEME_STYLES}.${buttonType}.${LAYOUT_MOBILE}.${prop}`,
        value: null,
      });
    }
    onChange(updates);
  };

  const createTabStateSetter = (buttonType) => (newState) => {
    setUiState((prev) => ({
      ...prev,
      [buttonType === BUTTON_PANEL_STATE_PRIMARY
        ? BUTTON_UI_STATE_PRIMARY_TAB
        : BUTTON_UI_STATE_SECONDARY_TAB]: newState,
    }));
  };

  const handleTabClick = (buttonType, newState) => {
    if (newState === BUTTON_PANEL_TAB_STATE_HOVER && selectedLayout === LAYOUT_MOBILE) {
      UIActions.selectActiveLayout({ layout: LAYOUT_DESKTOP, from: "panel" });
    }

    const setTabState = createTabStateSetter(buttonType);
    setTabState(newState);
  };

  return (
    <div className={styles.root}>
      {buttonsTypeMap.map((buttonType) => {
        const tabState =
          buttonType === BUTTON_PANEL_STATE_PRIMARY
            ? uiState[BUTTON_UI_STATE_PRIMARY_TAB]
            : uiState[BUTTON_UI_STATE_SECONDARY_TAB];
        const buttonThemeStyles = buttonStyles.get(buttonType);
        const themeChangeHandler = createThemeChangeHandler(buttonType);
        const themeInputHandler = createThemeChangeHandler(buttonType);

        const styleSync = getStyleSync(buttonType);
        const { addToggle } = styleSync;

        let mappedElement;
        try {
          mappedElement = MapElementProperties(
            buttonThemeStyles,
            null,
            null,
            selectedLayout,
            buttonPropMetaMapOverride,
          );

          const layoutProps = mappedElement.get(selectedLayout);
          if (layoutProps) {
            const updatedLayoutProps = layoutProps.map((prop) => {
              const updatedProp = {
                ...prop,
                inputEventHandlers: ["onSIChange", "onSIInput", "onSIBeforeChange"],
                onSIChange: themeChangeHandler(prop.name),
                onSIInput: themeInputHandler(prop.name),
                onSIBeforeChange: themeChangeHandler(prop.name),
                customPalette: palette,
                ...addToggle(prop.name),
              };

              if (prop.subProps) {
                const updatedSubProps = Object.keys(prop.subProps).reduce((acc, subPropKey) => {
                  const subProp = prop.subProps[subPropKey];
                  acc[subPropKey] = {
                    ...subProp,
                    inputEventHandlers: ["onSIChange", "onSIInput", "onSIBeforeChange"],
                    onSIChange: themeChangeHandler(subProp.name),
                    onSIInput: themeInputHandler(subProp.name),
                    onSIBeforeChange: themeChangeHandler(subProp.name),
                    customPalette: palette,
                    ...addToggle(subProp.name),
                  };
                  return acc;
                }, {});
                updatedProp.subProps = updatedSubProps;
              }

              return updatedProp;
            });

            mappedElement = mappedElement.set(selectedLayout, updatedLayoutProps);
          }
        } catch (error) {
          // fallback to empty property list if mapping fails
          mappedElement = fromJS({
            [selectedLayout]: [],
            isLocked: false,
          });
        }

        // filters blur from design settings but keeps it in property panel
        const layoutProps =
          mappedElement
            .get(selectedLayout)
            ?.filter((prop) => buttonStateFilter(prop.name, tabState))
            ?.filter((prop) => prop.name !== BLUR && prop.name !== HOVER_BLUR) || [];

        const isNormalTab = tabState !== BUTTON_STATE_HOVER;
        const pendingPropNames = pendingCustomizations[buttonType] || [];

        const defaultInputs = isNormalTab
          ? layoutProps.filter((prop) => DEFAULT_NORMAL_TAB_PROPS.includes(prop.name))
          : layoutProps.filter((prop) => DEFAULT_HOVER_TAB_PROPS.includes(prop.name));

        const otherProps = isNormalTab
          ? layoutProps.filter((prop) => !DEFAULT_NORMAL_TAB_PROPS.includes(prop.name))
          : layoutProps.filter((prop) => !DEFAULT_HOVER_TAB_PROPS.includes(prop.name));

        const customProps = otherProps.filter(
          (prop) => !prop.isInherited || pendingPropNames.includes(prop.name),
        );

        const unCustomizedProps = otherProps.filter((prop) => {
          if (!prop.isInherited || pendingPropNames.includes(prop.name)) {
            return false;
          }

          return true;
        });

        return (
          <div
            className={styles["section-container"]}
            data-open={uiState[BUTTON_UI_STATE_SELECTED_PANEL] === buttonType}
            key={buttonType}
          >
            <PropertyPanelSection
              title={buttonType}
              open={uiState[BUTTON_UI_STATE_SELECTED_PANEL] === buttonType}
              onClick={() =>
                setUiState((prev) => ({ ...prev, [BUTTON_UI_STATE_SELECTED_PANEL]: buttonType }))
              }
            >
              <div slot="title-component" style={{ marginRight: "0.5rem" }}>
                <ButtonStateTabset
                  buttonState={tabState}
                  setButtonState={handleTabClick}
                  buttonType={buttonType}
                />
              </div>

              {defaultInputs.map((prop) => (
                <DynamicInput
                  key={prop.name}
                  propData={{ ...prop, removeCustomization: undefined }}
                  layout={selectedLayout}
                  shift="right"
                />
              ))}
              {customProps.map((prop) => {
                const removeCustomization = () => {
                  handleRemoveCustomization(buttonType, prop.name);
                };
                return (
                  <DynamicInput
                    key={prop.name}
                    propData={{ ...prop, removeCustomization }}
                    layout={selectedLayout}
                    shift="right"
                  />
                );
              })}

              <DesignSettingsCustomizeMenu
                items={unCustomizedProps.map(({ name, label }) => ({ value: name, label }))}
                onSelect={(name) => handleAddCustomization(buttonType, name)}
                disabled={mappedElement.get("isLocked")}
                testId={`button-customize-${buttonType}`}
              />
            </PropertyPanelSection>
          </div>
        );
      })}
    </div>
  );
};

export default DesignSettingsButtonsStyles;
